import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { Form } from '@unform/web';
import { useField } from '@unform/core';
import { ImFloppyDisk } from 'react-icons/im';
import { isEqual } from 'lodash';
import * as Yup from 'yup';

import {
  Button,
  WrapperFlex,
  Title,
  Breadcrumbs,
} from '@unimed-fesp/components';

import {
  isValidMesmoTelefone,
  isValidMesmoCelular,
  validationPattern,
} from '../../utils/validation';
import { isContratante } from '../../utils/userRole';

import {
  TipoTelefoneEnum,
  PermissaoContratanteEnum,
  TipoStatusEnum,
  TipoPerfilUsuarioEnum,
} from '../../enums';

import FadeIn from '../../components/atoms/FadeIn';

import TabsFormConfiguracoes from '../../components/organisms/TabsFormConfiguracoes';
import ModalSuccess from '../../components/organisms/ModalSuccess';
import ModalError from '../../components/organisms/ModalError';

import { Creators as UsuarioActions } from '../../store/ducks/usuario';
import { Creators as AuthActions } from '../../store/ducks/auth';
import { senhaPattern } from '../../utils/patterns';
import history from '../../services/history';

import { Container } from './styles';
import theme from '../../styles/theme';

import useParametrizacao from '../../hooks/useParametrizazao';

function Configuracoes() {
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const { idUsuarioLogado, role } = useSelector((state) => state.auth.usuario);
  const { usuario } = useSelector((state) => state.auth);

  const {
    getUsuarioById,
    getContatoById,
    getDadosAcessoById,
    patchDadosGerais,
    patchDadosAcesso,
    patchSenha,
  } = useSelector((state) => state.usuario);
  const { salvarParametrizacao, hasAlteracao } = useParametrizacao(
    idUsuarioLogado,
    role
  );

  const passwordSchema = Yup.object().shape({
    senha: Yup.string().trim().required('Necessário preencher a senha atual.'),
    novaSenha: Yup.string()
      .trim()
      .matches(
        validationPattern.password.regexValidate,
        'A nova senha não atende as especificações.'
      )
      .matches(
        validationPattern.password.symbolValidate,
        'É necessário ao menos um símbolo para a nova senha.'
      )
      .trim()
      .matches(senhaPattern.pattern, senhaPattern.message)
      .notOneOf([Yup.ref('senha')], 'A nova senha deve ser diferente da atual')
      .required('Necessário preencher a nova senha.'),
    confirmacaoSenha: Yup.string()
      .trim()
      .matches(
        validationPattern.password.regexValidate,
        'A confirmação de senha não atende as especificações.'
      )
      .matches(
        validationPattern.password.symbolValidate,
        'É necessário ao menos um símbolo para a nova senha.'
      )
      .matches(senhaPattern.pattern, senhaPattern.message)
      .oneOf(
        [Yup.ref('novaSenha')],
        'A confirmação de senha deve ser igual a nova senha'
      )
      .notOneOf(
        [Yup.ref('senha')],
        'A confirmação de senha deve ser diferente da atual'
      )
      .required('Necessário preencher a confirmação senha.'),
  });

  const editarUsuarioParam = {
    idUsuario: parseInt(idUsuarioLogado, 10),
    nome: getUsuarioById.response.nome || '',
    matricula: getUsuarioById.response.matricula || '',
    tipoPerfil: getUsuarioById.response.tipoPerfil || '',
    permissao:
      PermissaoContratanteEnum.returnPermissaoForTipoPerfilUsuario[
        getUsuarioById.response.tipoPerfil
      ] || '',
  };

  const editarContatosParams = {
    contatos: [
      {
        tipo: TipoTelefoneEnum.returnNameForName.Residencial,
        numero: '',
      },
    ],
  };

  const editarDadosAcessoParams = {
    nome: getDadosAcessoById.response.nome || '',
    email: getDadosAcessoById.response.email || '',
    senha: '',
    novaSenha: '',
    confirmacaoSenha: '',
  };

  const [usuarioParams, setUsuarioParams] = useState(editarUsuarioParam);
  const [contatoParams, setContatoParams] = useState(editarContatosParams);
  const [passwordParams, setPasswordParams] = useState();
  const [originalContatoParams, setOriginalContatoParams] = useState(
    editarContatosParams
  );
  const [dadosAcessoParams, setDadosAcessoParams] = useState(
    editarDadosAcessoParams
  );
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [openModalAcaoUsuario, setOpenModalAcaoUsuario] = useState({
    success: false,
    acaoUsuario: '',
    message: '',
  });
  const [modalError, setModalError] = useState({
    error: false,
    message: '',
  });
  const [activeTab, setActiveTab] = useState('dadosGerais');
  const [disableNameInput, setDisableNameInput] = useState(true);
  function IsStateChangeFromOriginal(prevState, newState) {
    return !isEqual(prevState, newState);
  }

  useEffect(() => {
    if (usuario.role === 'Adm') {
      setDisableNameInput(false);
    }
    batch(() => {
      dispatch(UsuarioActions.getUsuarioByIdRequest(idUsuarioLogado));
      dispatch(UsuarioActions.getContatoByIdRequest(idUsuarioLogado));
      dispatch(UsuarioActions.getDadosAcessoByIdRequest(idUsuarioLogado));
    });
  }, []);

  useEffect(() => {
    if (getUsuarioById.response) setUsuarioParams(editarUsuarioParam);
  }, [getUsuarioById.response]);

  useEffect(() => {
    if (getContatoById.response.length > 0) {
      const contatos = [];
      getContatoById.response.map((contato) => {
        contatos.push({
          tipo: contato.tipoContato,
          numero: contato.nrDdd.toString().concat(contato.nrNumero),
        });
      });

      setOriginalContatoParams({ contatos });
      setContatoParams({ contatos });
    } else {
      setContatoParams(editarContatosParams);
    }
  }, [getContatoById.response]);

  useEffect(() => {
    if (getDadosAcessoById.response)
      setDadosAcessoParams(editarDadosAcessoParams);
  }, [getDadosAcessoById.response]);

  function IsSubmitDadosGerais() {
    if (
      IsStateChangeFromOriginal(editarUsuarioParam, usuarioParams) &&
      !IsStateChangeFromOriginal(originalContatoParams, contatoParams)
    ) {
      return IsStateChangeFromOriginal(editarUsuarioParam, usuarioParams);
    }
    if (
      !IsStateChangeFromOriginal(editarUsuarioParam, usuarioParams) &&
      IsStateChangeFromOriginal(originalContatoParams, contatoParams) &&
      contatoParams.contatos.every((c) => isValidContato(c.numero, c.tipo))
    ) {
      return (
        IsStateChangeFromOriginal(originalContatoParams, contatoParams) &&
        contatoParams.contatos.every((c) => c.numero !== '') &&
        contatoParams.contatos.every((c) => isValidContato(c.numero, c.tipo))
      );
    }
    if (
      IsStateChangeFromOriginal(editarUsuarioParam, usuarioParams) &&
      IsStateChangeFromOriginal(originalContatoParams, contatoParams) &&
      contatoParams.contatos.every((c) => isValidContato(c.numero, c.tipo))
    ) {
      return (
        IsStateChangeFromOriginal(editarUsuarioParam, usuarioParams) ||
        (IsStateChangeFromOriginal(originalContatoParams, contatoParams) &&
          contatoParams.contatos.every((c) => c.numero !== '') &&
          contatoParams.contatos.every((c) => isValidContato(c.numero, c.tipo)))
      );
    }
  }

  function IsSubmitDadosAcesso() {
    return IsStateChangeFromOriginal(
      editarDadosAcessoParams,
      dadosAcessoParams
    );
  }

  const setButtonDisabledForTab = useCallback(
    (tabActive) => {
      const tabs = {
        dadosGerais: IsSubmitDadosGerais(),
        dadosAcesso: IsSubmitDadosAcesso(),
        parametros: hasAlteracao,
      };

      return tabs[tabActive];
    },
    [IsSubmitDadosAcesso, IsSubmitDadosGerais, hasAlteracao]
  );

  useEffect(() => {
    if (setButtonDisabledForTab(activeTab)) setIsButtonDisabled(false);
    else setIsButtonDisabled(true);
  }, [
    activeTab,
    usuarioParams,
    contatoParams,
    dadosAcessoParams,
    setButtonDisabledForTab,
  ]);

  function isValidContato(contato, tipo) {
    const validContato = {
      Residencial: isValidMesmoTelefone(contato),
      Celular: isValidMesmoCelular(contato),
    };

    return validContato[tipo];
  }

  function preparaDadosGerais(data) {
    const formUsuarioParams = {
      idUsuario: parseInt(idUsuarioLogado, 10),
      nome: data.nome,
      matricula: data.matricula ? data.matricula : '',
      tipoPerfil: TipoPerfilUsuarioEnum.returnNameForNameFormatted[data.perfil],
      permissao: data.permissao ? data.permissao : '',
    };

    const formContatoParams = {
      contatos: [],
    };

    if (data.contato?.length > 0)
      data.contato.map((c) => {
        formContatoParams.contatos.push({
          numero: c.numero,
          tipo: c.tipo,
        });
      });

    return { formUsuarioParams, formContatoParams };
  }

  function bindPermissaoToPerfil(data) {
    return TipoPerfilUsuarioEnum.returnNameForNameFormatted[data.perfil] !==
      TipoPerfilUsuarioEnum.returnNameForName.Adm
      ? PermissaoContratanteEnum.returnTipoPerfilUsuarioForPermissao[
          data.permissao
        ]
      : TipoPerfilUsuarioEnum.returnNameForNameFormatted[data.perfil];
  }

  function dadosGeraisSubmit(data) {
    const { formUsuarioParams } = preparaDadosGerais(data);
    const formContatoParams = { contatos: [] };
    if (
      IsStateChangeFromOriginal(formUsuarioParams, editarUsuarioParam) ||
      (IsStateChangeFromOriginal(contatoParams, editarContatosParams) &&
        contatoParams.contatos.every((c) => c.numero !== '') &&
        contatoParams.contatos.every((c) => isValidContato(c.numero, c.tipo)))
    ) {
      formContatoParams.contatos = contatoParams.contatos.map((c) => {
        return {
          ddd: c.numero.substring(0, 2),
          numero: c.numero.substring(2),
          tipo: c.tipo,
        };
      });

      formUsuarioParams.tipoPerfil = bindPermissaoToPerfil(data);

      const { role } = usuario;
      if (isContratante(role)) {
        dispatch(
          UsuarioActions.patchRegistrarContatoRequest(
            formContatoParams,
            formUsuarioParams.idUsuario
          )
        );
      } else {
        dispatch(
          UsuarioActions.patchDadosGeraisRequest(
            formUsuarioParams,
            formContatoParams
          )
        );
      }
    }

    // if (
    //     IsStateChangeFromOriginal(
    //         formContatoParams,
    //         editarContatosParams
    //     ) &&
    //     formContatoParams.contatos.every((c) => c.numero !== '') &&
    //     formContatoParams.contatos.every((c) =>
    //         isValidContato(c.numero, c.tipo)
    //     )
    // ) {
    //     formContatoParams.contatos.map((c) => {
    //         c.ddd = c.numero.substring(0, 2);
    //         c.numero = c.numero.substring(2);
    //     });

    //     dispatch(
    //         UsuarioActions.patchRegistrarContatoRequest(
    //             formContatoParams,
    //             idUsuarioLogado
    //         )
    //     );
    // }
  }

  async function dadosAcessoSubmit(data) {
    try {
      // if (isAdmin(usuario.role) && data.email !== '') {
      //   await schema.validate(data, {
      //     abortEarly: false,
      //   });

      //   const params = {
      //     email: data.email,
      //     idUsuario: idUsuarioLogado,
      //   };
      //   console.log(params);
      //   // dispatch(UsuarioActions.patchDadosAcessoRequest(params));
      // }

      await passwordSchema.validate(data, {
        abortEarly: false,
      });

      setPasswordParams({ senha: data.novaSenha });
      dispatch(
        UsuarioActions.patchSenhaRequest(
          idUsuarioLogado,
          data.senha,
          data.novaSenha,
          data.confirmacaoSenha
        )
      );

      formRef.current.setErrors({});
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errorMessages = {};

        error.inner.forEach((err) => {
          errorMessages[err.path] = err.message;
        });

        formRef.current.setErrors(errorMessages);
      }
    }
  }

  function handleSubmit(data) {
    switch (activeTab) {
      case 'dadosGerais':
        dadosGeraisSubmit(data);
        break;
      case 'dadosAcesso':
        dadosAcessoSubmit(data);
        break;
      case 'parametros':
        salvarParametrizacao();
        break;
      default:
    }
  }

  function setUsuarioActionToDispatch(action, params) {
    const actions = {
      PendenteLiberacao: UsuarioActions.patchLiberarAcessoRequest(params),
      Ativo: UsuarioActions.patchStatusRequest(
        params,
        TipoStatusEnum.returnNameForName.Inativo
      ),
      Inativo: UsuarioActions.patchStatusRequest(
        params,
        TipoStatusEnum.returnNameForName.Ativo
      ),
    };

    return actions[action];
  }

  function handleAcaoUsuarioById(idUsuario, tipoAcao) {
    const params = [
      {
        idUsuario,
      },
    ];
    const actionUsuario = setUsuarioActionToDispatch(tipoAcao, params);

    dispatch(actionUsuario);
  }

  function handleModalUsuarioAcao() {
    setOpenModalAcaoUsuario({
      success: false,
      message: 'solicitação de liberação enviada.',
    });
    if (patchDadosGerais.success)
      dispatch(UsuarioActions.patchDadosGeraisRenew());
    if (patchSenha.success) dispatch(UsuarioActions.patchSenhaRenew());
    if (patchDadosAcesso.success)
      dispatch(UsuarioActions.patchDadosAcessoRenew());
  }

  function handleModalError() {
    setModalError({
      error: !modalError.error,
      message: 'erro ao salvar os dados gerais.',
    });

    if (patchDadosGerais.isError)
      dispatch(UsuarioActions.patchDadosGeraisRenew());
    if (patchSenha.isError) dispatch(UsuarioActions.patchSenhaRenew());
    if (patchDadosAcesso.isError)
      dispatch(UsuarioActions.patchDadosAcessoRenew());
  }

  function redirectTo() {
    history.push('/novo/lista');
  }

  function handleActiveTab(tab) {
    setActiveTab(tab);
  }

  useEffect(() => {
    if (patchDadosGerais.isError)
      setModalError({
        error: !modalError.error,
        message: 'erro ao salvar os dados gerais.',
      });
  }, [patchDadosGerais.isError]);

  useEffect(() => {
    if (patchDadosGerais.success)
      setOpenModalAcaoUsuario({
        success: !openModalAcaoUsuario.success,
        message: 'alteração de dados gerais realizada.',
      });
  }, [patchDadosGerais.success]);

  useEffect(() => {
    if (patchSenha.isError) {
      const OLD_USER_MESSAGE =
        'Usuário no padrão antigo, entre em contato com o setor de Cadastro para criar um novo usuário.';

      const message =
        patchSenha.error[0] === OLD_USER_MESSAGE
          ? OLD_USER_MESSAGE
          : 'erro ao alterar a senha.';

      setModalError({
        error: !modalError.error,
        message,
      });
    }
  }, [patchSenha.isError]);

  useEffect(() => {
    if (patchSenha.success) {
      setOpenModalAcaoUsuario({
        success: !openModalAcaoUsuario.success,
        message: 'alteração de senha realizada.',
      });

      const renewSessionParams = {
        userId: usuario.login,
        password: passwordParams.senha,
        // userType: usuario.role,
        grantType: 'password',
      };

      dispatch(AuthActions.postLoginRequest(renewSessionParams));
    }
  }, [patchSenha.success]);

  useEffect(() => {
    if (patchDadosAcesso.isError) {
      setModalError({
        error: !modalError.error,
        message: 'erro ao alterar os dados de acesso.',
      });
    }
  }, [patchDadosAcesso.isError]);

  useEffect(() => {
    if (patchDadosAcesso.success)
      setOpenModalAcaoUsuario({
        success: !openModalAcaoUsuario.success,
        message: 'alteração de dados de acesso realizada.',
      });
  }, [patchDadosAcesso.success]);

  return (
    <Container>
      <FadeIn>
        <Title
          text="configurações"
          color={theme.titleGray}
          fontSize={38}
          marginBottom={8}
        />
        <Breadcrumbs
          crumbs={[
            { path: 'novo' },
            {
              path: 'configurações',
              onNavigate: () => history.push('/novo/configuracoes'),
            },
          ]}
        />
        <Form ref={formRef} onSubmit={handleSubmit}>
          <TabsFormConfiguracoes
            usuarioParams={usuarioParams}
            setUsuarioParams={setUsuarioParams}
            contatoParams={contatoParams}
            setContatoParams={setContatoParams}
            dadosAcessoParams={dadosAcessoParams}
            setDadosAcessoParams={setDadosAcessoParams}
            usuarioDadosGerais={getUsuarioById}
            usuarioDadosAcesso={getDadosAcessoById.response}
            handleAcaoUsuarioById={handleAcaoUsuarioById}
            handleActiveTab={handleActiveTab}
            disableNameInput={disableNameInput}
            useField={useField}
          />
          <WrapperFlex marginTop={30} justifySpaceBetween alignCenter>
            <Button
              tipo="tertiary"
              type="button"
              justifyCenter
              alignCenter
              width={100}
              font="16px unimed_slabregular"
              onClick={redirectTo}
            >
              cancelar
            </Button>
            <Button
              tipo="primary"
              width={100}
              paddingTop={1}
              paddingRight={1}
              paddingBottom={1}
              paddingLeft={1}
              font="16px unimed_slabregular"
              disabled={isButtonDisabled}
              justifySpaceAround
              alignCenter
              type="submit"
            >
              <ImFloppyDisk />
              salvar
            </Button>
          </WrapperFlex>
        </Form>
        <ModalSuccess
          onClick={handleModalUsuarioAcao}
          text={openModalAcaoUsuario.message}
          isOpen={openModalAcaoUsuario.success}
        />
        <ModalError
          onClick={handleModalError}
          text={modalError.message}
          isOpen={modalError.error}
        />
      </FadeIn>
    </Container>
  );
}

export default Configuracoes;
